import React, { useEffect, useMemo } from 'react';
import Toggle from 'src/components/Toggle/Toggle';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import Callout from 'src/components/InformationCallout/Callout';
import CustomPagination from 'src/components/CustomPagination/CustomPagination';
import { useClientPagination } from 'src/components/CustomPagination/useClientPagination';
import { StagedEntry } from 'src/components/ContactsTable/EmailSelfServiceDialogue/CustomHooks/useEmailSelfServiceFormState/useEmailSelfServiceFormState';
import { useContactDisplay } from 'src/components/ContactsTable/EmailSelfServiceDialogue/CustomHooks/useContactDisplay';
import './step-review-changes.scss';

interface StepReviewChangesProps {
  stagedEntries: StagedEntry[];
  unchangedContacts: StagedEntry[];
  willAllowCount: number;
  willDisallowCount: number;
  isSubmitting: boolean;
  error: string | null;
  onBulkSetStaged: (allow: boolean) => void;
}

interface StagedRowProps {
  entry: StagedEntry;
  isUnchanged: boolean;
}

const StagedRow = React.memo(({ entry, isUnchanged }: StagedRowProps) => {
  const { contact, newValue } = entry;
  const { fullName, roleLabels } = useContactDisplay(contact);

  const rowClassName: string = isUnchanged
    ? 'src-row src-row-unchanged'
    : 'src-row';

  const unchangedLabel: string = newValue
    ? 'Already enabled — no change'
    : 'Already disabled — no change';

  return (
    <div className={rowClassName}>
      <div className="src-row-identity">
        <div className="src-row-name">{fullName}</div>
        <div className="src-row-email">{contact.email ?? '—'}</div>
        {isUnchanged && (
          <div className="src-row-unchanged-label">{unchangedLabel}</div>
        )}
      </div>
      <div className="src-row-roles">
        {roleLabels.map((roleLabel: string) => (
          <CategoryPill key={roleLabel} text={roleLabel} size="small" />
        ))}
      </div>
    </div>
  );
});

StagedRow.displayName = 'StagedRow';

const StepReviewChanges = ({
  stagedEntries,
  unchangedContacts,
  willAllowCount,
  willDisallowCount,
  isSubmitting,
  error,
  onBulkSetStaged,
}: StepReviewChangesProps) => {
  const totalStagedCount: number = stagedEntries.length;
  const unchangedCount: number = unchangedContacts.length;
  const showEmpty: boolean = totalStagedCount === 0;

  const {
    currentPage,
    totalPages,
    paginatedItems,
    setCurrentPage,
    resetToFirstPage,
  } = useClientPagination<StagedEntry>({
    items: stagedEntries,
    itemsPerPage: 12,
  });

  useEffect(() => {
    resetToFirstPage();
  }, [totalStagedCount]);

  const showPagination: boolean = totalPages > 1;

  const allAllow: boolean =
    totalStagedCount > 0 &&
    stagedEntries.every((entry: StagedEntry) => entry.newValue === true);

  const handleBulkToggle = (currentValue: boolean): void => {
    if (isSubmitting || totalStagedCount === 0) {
      return;
    }

    onBulkSetStaged(!currentValue);
  };

  const disallowEndClassName: string = allAllow
    ? 'src-bulk-toggle-end src-bulk-toggle-end-disallow'
    : 'src-bulk-toggle-end src-bulk-toggle-end-disallow src-bulk-toggle-end-active';

  const allowEndClassName: string = allAllow
    ? 'src-bulk-toggle-end src-bulk-toggle-end-allow src-bulk-toggle-end-active'
    : 'src-bulk-toggle-end src-bulk-toggle-end-allow';

  const listClassName: string = allAllow
    ? 'src-list src-list-allow'
    : 'src-list src-list-disallow';

  const unchangedIds: Set<string> = useMemo(() => {
    return new Set(
      unchangedContacts.map((entry: StagedEntry) => entry.contact.id)
    );
  }, [unchangedContacts]);

  return (
    <div className="src-step">
      <div className="src-header">
        <div className="src-summary">
          <div className="src-summary-pill src-summary-pill-allow">
            <span className="src-summary-pill-count">{willAllowCount}</span>
            <span className="src-summary-pill-label">will allow</span>
          </div>
          <div className="src-summary-pill src-summary-pill-disallow">
            <span className="src-summary-pill-count">{willDisallowCount}</span>
            <span className="src-summary-pill-label">will disallow</span>
          </div>
          <div className="src-summary-pill src-summary-pill-unchanged">
            <span className="src-summary-pill-count">{unchangedCount}</span>
            <span className="src-summary-pill-label">unchanged</span>
          </div>
        </div>
        <div className="src-bulk-toggle">
          <span className={disallowEndClassName}>Disallow</span>
          <Toggle label="" value={allAllow} onToggle={handleBulkToggle} />
          <span className={allowEndClassName}>Allow</span>
        </div>
        <Callout
          variant="info"
          title="One toggle controls every contact below."
          subtitle="Individual rows show the resulting state and aren't editable here."
        />
      </div>
      <div className={listClassName}>
        {showEmpty && (
          <div className="src-state">
            <div className="src-state-heading">Nothing staged</div>
            <div className="src-state-body">
              Go back and pick contacts to manage.
            </div>
          </div>
        )}
        {!showEmpty &&
          paginatedItems.map((entry: StagedEntry) => {
            const isUnchanged: boolean = unchangedIds.has(entry.contact.id);

            return (
              <StagedRow
                key={entry.contact.id}
                entry={entry}
                isUnchanged={isUnchanged}
              />
            );
          })}
      </div>
      {showPagination && (
        <div className="src-pagination">
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            disabled={isSubmitting}
          />
        </div>
      )}
      {error && (
        <div className="src-error">
          <div className="src-error-heading">
            We couldn't apply your changes
          </div>
          <div className="src-error-body">{error}</div>
        </div>
      )}
    </div>
  );
};

export default StepReviewChanges;
